'use client';

import type { ReactNode } from 'react';

type SettingsInlineRowProps = {
  /** The controls on this line, left to right. */
  children: ReactNode;
  /**
   * Trailing control pinned after the fields — a remove button on a repeatable
   * row. Kept out of `children` so it always sits last and never wraps into the
   * middle of the field run.
   */
  action?: ReactNode;
  className?: string;
  align?: 'start' | 'center' | 'end';
};

type SettingsInlineStackProps = {
  children: ReactNode;
  className?: string;
};

/**
 * One line of a repeatable list inside a settings row's control column — the
 * prototype's `.vrow`: a few fields side by side, then an optional trailing
 * action.
 *
 * The sibling of `SettingsWorkingHoursRow`, which is the same idea specialised to
 * a from/to(/for) time range with fixed column widths so a week of days lines up.
 * This one is the general case: it bounds nothing, so each caller sizes its own
 * fields with `SettingsField`.
 *
 * `items-end` rather than `items-center`, because a repeatable row mixes fields
 * that carry a label or an error message with ones that do not, and aligning on
 * the baseline of the controls keeps the remove button level with the inputs
 * rather than floating against the tallest child.
 *
 * TWO NESTED FLEX CONTAINERS, not one flat wrapping row: the outer row does not
 * wrap, so `action` — a small icon-only remove button — can never fall onto its
 * own line below the fields, where it used to read as a stray empty checkbox
 * (flagged by design). `children` gets its own inner `flex-wrap` container that
 * absorbs the wrapping instead, so when space runs out the fields wrap among
 * themselves while `action` stays pinned at the row's trailing edge,
 * bottom-aligned with whichever field is tallest.
 */
export const SettingsInlineRow = ({ children, action, className, align = 'end' }: SettingsInlineRowProps) => {
  const alignClass = align === 'start' ? 'items-start' : align === 'center' ? 'items-center' : 'items-end';
  return (
    <div
      data-lula-ds=""
      className={`flex ${alignClass} gap-space-8 font-sans${className ? ` ${className}` : ''}`}
    >
      <div className={`flex min-w-0 flex-1 flex-wrap ${alignClass} gap-space-8`}>{children}</div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
};

/**
 * Stacks `SettingsInlineRow`s (and the "add another" button that closes the list)
 * vertically — the prototype's `.vstack`.
 *
 * A settings row's control column lays its children out in a wrapping flex ROW,
 * so a repeatable list needs its own column container to read as a list.
 */
export const SettingsInlineStack = ({ children, className }: SettingsInlineStackProps) => (
  <div
    data-lula-ds=""
    className={`flex w-full min-w-0 flex-col items-start gap-space-12 font-sans${
      className ? ` ${className}` : ''
    }`}
  >
    {children}
  </div>
);

export default SettingsInlineRow;
